import i18n, { TFunction } from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { resolveChinese, resolveEnglish, resolveFrench, resolveSpanish } from './lib/language';
import { replaceAllSpaces } from './lib/utils';
import { grammarFormat } from './models/newspaper/grammar/grammarFormat';

// See also https://spdlybra.nintendo.co.jp/confluence/pages/viewpage.action?pageId=741611370

const whitelist = [
  'de-DE',
  'en-GB',
  'en-US',
  'es-ES',
  'es-US',
  'fr-CA',
  'fr-FR',
  'it-IT',
  'ja-JP',
  'ko-KR',
  'nl-NL',
  'ru-RU',
  'zh-CN',
  'zh-TW'
];

const blancoVersion = process.env.REACT_APP_VERSION as 'string';

export default i18n;

export function initializeI18n(lng: string) {
  return i18n
    .use(Backend)
    .use(initReactI18next)
    .init({
      lng,
      whitelist,
      fallbackLng: {
        de: ['de-DE', 'en-US'],
        en: ['en-US', 'en-GB'],
        es: ['es-ES', 'es-US', 'en-US'],
        fr: ['fr-FR', 'fr-CA', 'en-US'],
        it: ['it-IT', 'en-US'],
        ja: ['ja-JP', 'en-US'],
        ko: ['ko-KR', 'en-US'],
        nl: ['nl-NL', 'en-US'],
        ru: ['ru-RU', 'en-US'],
        zh: ['zh-CN', 'zh-TW', 'en-US'],
        default: ['en-US']
      },
      load: 'currentOnly',
      ns: [
        'translation',
        'coral',
        'notification',
        'passport',
        'chat',
        'best_friends',
        'my_design',
        'settings',
        'dream',
        'reaction',
        'catalog',
        'fortune',
        'life_supports',
        'resort',
        'newspaper',
        'newspaper_normal_article'
      ],
      keySeparator: false,
      interpolation: {
        format: grammarFormat, // 文法対応用
        escapeValue: false // react already safes from xss
      },
      debug: process.env.NODE_ENV !== 'production',
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        queryStringParams: { v: blancoVersion }
      }
    });
}

export async function setLanguage(language: string) {
  return i18n.changeLanguage(language);
}

export function resolveLanguage(language: string, naCountry: string): string {
  const [lang, country] = language.split('-');
  switch (lang) {
    case 'es':
      return resolveSpanish(country, naCountry);
    case 'en':
      return resolveEnglish(country, naCountry);
    case 'fr':
      return resolveFrench(country, naCountry);
    case 'zh':
      return resolveChinese(country, naCountry);
    case 'ja':
      return 'ja-JP';
    case 'de':
      return 'de-DE';
    case 'it':
      return 'it-IT';
    case 'nl':
      return 'nl-NL';
    case 'ru':
      return 'ru-RU';
    case 'ko':
      return 'ko-KR';
    default:
      return 'en-US';
  }
}

// ユーザの自由入力テキストがi18nextのテンプレートと競合してコンテンツがなくなってしまう問題のworkaround
export function interpolateFreeText(
  t: TFunction,
  label: string,
  paramKey: string,
  paramValue: string
) {
  const encoded = encodeURIComponent(paramValue);
  const translated = t(label, { [paramKey]: encoded });
  const decoded = decodeURIComponent(translated);
  return replaceAllSpaces(decoded);
}

export function getIslandRuby(
  language: string,
  displayId: number
): { suffix?: string; ruby?: string } {
  switch (language) {
    case 'ja-JP': {
      const suffix = '島';
      switch (displayId) {
        case 0:
          return { suffix, ruby: 'しま' };
        case 1:
          return { suffix, ruby: 'じま' };
        case 2:
        case 3:
          return { suffix, ruby: 'とう' };
        default:
          // unknown display id
          return {};
      }
    }
    case 'ko-KR':
      switch (displayId) {
        case 0:
        case 1:
        case 2:
          return { suffix: '섬' };
        case 3:
          return { suffix: '도' };
        default:
          // unknown display id
          return {};
      }
    case 'zh-CN':
      return { suffix: '岛' };
    case 'zh-TW':
      return { suffix: '島' };
    default:
      return {};
  }
}
